import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Image as ImageIcon, Upload, Trash2, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

// Mirrors the server allowlist in galleryMediaType.js — used only for immediate
// client-side feedback. The server re-derives the real type from file bytes and
// is the actual security boundary, so this is not relied on for anything else.
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm';
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const GalleryTab = ({ academyId, userRole }) => {
  const { theme } = useTheme();
  const canManage = userRole === 'owner' || userRole === 'admin';
  const fileInputRef = useRef(null);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reorderingId, setReorderingId] = useState(null);

  const loadGallery = useCallback(async () => {
    setLoading(true);
    try {
      const data = await academyAPI.gallery.list(academyId);
      setItems(data || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load gallery');
    } finally {
      setLoading(false);
    }
  }, [academyId]);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  const handleFileSelected = async (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (!files.length) return;

    // Oversized files are reported and skipped rather than failing the whole
    // selection — picking twenty photos and losing all of them to one large
    // file is a worse outcome than uploading the nineteen that are fine.
    const tooBig = files.filter(f => f.size > MAX_UPLOAD_BYTES);
    const toUpload = files.filter(f => f.size <= MAX_UPLOAD_BYTES);
    if (tooBig.length) {
      toast.error(
        tooBig.length === 1
          ? `${tooBig[0].name} is over 10MB and was skipped.`
          : `${tooBig.length} files are over 10MB and were skipped.`,
      );
    }
    if (!toUpload.length) return;

    setUploading(true);
    try {
      // Sequential, not Promise.all: these are 10MB uploads and the gallery
      // keeps an explicit sort order, so firing them all at once risks both
      // saturating the connection and landing them out of order.
      let uploaded = 0;
      for (const file of toUpload) {
        await academyAPI.gallery.upload(academyId, file);
        uploaded++;
      }
      toast.success(uploaded === 1 ? 'Uploaded to gallery' : `${uploaded} files uploaded`);
      loadGallery();
    } catch (error) {
      toast.error(error.message || 'Upload failed');
      loadGallery(); // whatever did land should still show
    } finally {
      setUploading(false);
    }
  };

  // Which item the confirm dialog is asking about — null when it is closed.
  // window.confirm was doing this before, which drops a browser-chrome alert
  // over the page: unthemed, unmovable, and blocking the whole tab while it is
  // open. ConfirmDialog is the same pattern the rest of the app already uses.
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const handleDelete = async () => {
    const itemId = pendingDeleteId;
    setPendingDeleteId(null);
    if (!itemId) return;
    try {
      await academyAPI.gallery.remove(academyId, itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Gallery item deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete gallery item');
    }
  };

  const handleMove = async (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const reordered = [...items];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setItems(reordered);
    setReorderingId(reordered[targetIndex].id);
    try {
      await academyAPI.gallery.reorder(academyId, reordered.map(item => item.id));
    } catch (error) {
      toast.error(error.message || 'Failed to reorder gallery');
      loadGallery();
    } finally {
      setReorderingId(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className={`text-2xl font-bold ${theme.text}`}>Gallery</h3>
          <p className={theme.textSecondary}>{items.length} photo{items.length === 1 ? '' : 's'}/video{items.length === 1 ? '' : 's'}</p>
        </div>
        {canManage && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              multiple
              className="hidden"
              onChange={handleFileSelected}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`flex items-center gap-x-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg disabled:opacity-50`}
            >
              {uploading ? <Loader2 size={20} className="animate-spin" /> : <Upload size={20} />}
              <span>{uploading ? 'Uploading...' : 'Upload'}</span>
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className={`${theme.card} text-center p-12 rounded-xl`}>
          <Loader2 size={32} className="mx-auto animate-spin opacity-50" />
        </div>
      ) : items.length === 0 ? (
        <div className={`${theme.card} text-center p-12 rounded-xl`}>
          <ImageIcon size={48} className="mx-auto mb-3 opacity-50" />
          <h5 className={`text-xl font-semibold ${theme.text} mb-2`}>No gallery items yet</h5>
          <p className={theme.textSecondary}>
            {canManage ? 'Upload photos or videos to showcase your academy.' : 'Check back later for photos and videos.'}
          </p>
          {canManage && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className={`mt-4 px-4 py-2 ${theme.buttonPrimary} rounded-lg`}
            >
              Upload
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, index) => (
            <div key={item.id} className={`${theme.card} rounded-xl overflow-hidden group relative`}>
              {item.type === 'video' ? (
                <video src={resolveImageUrl(item.url)} controls className="w-full h-48 object-cover bg-black" />
              ) : (
                <img src={resolveImageUrl(item.url)} alt={item.caption || 'Academy gallery'} className="w-full h-48 object-cover" />
              )}
              {item.caption && (
                <p className={`px-3 py-2 text-sm ${theme.textSecondary}`}>{item.caption}</p>
              )}
              {canManage && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleMove(index, -1)}
                    disabled={index === 0 || reorderingId === item.id}
                    className="p-2 bg-black/60 text-white rounded-full hover:bg-black/80 disabled:opacity-40"
                    aria-label="Move earlier"
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    onClick={() => handleMove(index, 1)}
                    disabled={index === items.length - 1 || reorderingId === item.id}
                    className="p-2 bg-black/60 text-white rounded-full hover:bg-black/80 disabled:opacity-40"
                    aria-label="Move later"
                  >
                    <ArrowDown size={14} />
                  </button>
                  <button
                    onClick={() => setPendingDeleteId(item.id)}
                    className="p-2 bg-black/60 text-white rounded-full hover:bg-red-600"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={pendingDeleteId !== null}
        title="Delete this gallery item?"
        message="This can't be undone."
        confirmLabel="Delete"
        confirmDanger
        onConfirm={handleDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
};

export default GalleryTab;
